<template>
  <div class="map">
    <div id='general-map' class='map-container' style="height: calc(100vh - 50px)"></div>
    <div v-if="layersUpdating > 0" style="position: fixed; bottom: 0; left: 50%; transform: translateX(-50%); vertical-align: middle; z-index: 999999;" class="bg-info text-white p-1 px-3">
      <small><translate>Updating</translate>&nbsp;<b-spinner small style="vertical-align: middle;" /></small>
    </div>

    <b-modal id="modalShippingsCreateFromItem" size="xl" ref="modalShippingsCreateFromItem" hide-footer>
      <div slot="modal-title" v-translate>New Shipment</div>
      <div slot="modal-header-close"><img src="@/assets/img/ic_close.svg" alt="×" /></div>
      <shipping-create-from-item :new-shipping="newB2bShipping" @success="shippingCreated" />
    </b-modal>

    <items-shared />

    <visits-overview
      ref="visitsOverview"
      :all-couriers="allCouriers"
      :base-data="baseData"
      :company-country-config="companyCountryConfig"
      @assigned="handleAssignmentDone"
    />
  </div>
</template>

<script>
import 'leaflet/dist/leaflet.css'
import MapService from '@/services/MapService'
import MapLayerControl from '@/components/MapsV2/Controls/MapLayerControl'
import OpenInGoogleMaps from '@/components/MapsV2/Controls/OpenInGoogleMaps'
import TilesLayerControl from '@/components/MapsV2/Controls/TilesLayerControl'
import FitBounds from '@/components/MapsV2/Controls/FitBounds'
import AllInOneSearch from '@/components/Maps/AllInOneSearch'
import MapLayersConfigMixin from '@/mixins/mapLayersConfig.mixin'
import { cloneDeep, debounce } from 'lodash'
import ShippingCreateFromItem from '@/components/Shippings/CreateFromItem'
import ItemsShared from '@/components/Items/Shared'
import VisitsOverview from '@/components/MapsV2/Modals/VisitsOverview'
import ToursMixin from '@/mixins/tours.mixin'

export default {
  name: 'MapNew',
  mixins: [ToursMixin, MapLayersConfigMixin],
  components: {
    ShippingCreateFromItem,
    ItemsShared,
    MapLayerControl,
    OpenInGoogleMaps,
    TilesLayerControl,
    FitBounds,
    AllInOneSearch,
    VisitsOverview
  },
  data() {
    return {
      mapInstance: null,
      center: { lat: -34.6037, lng: -58.3816 }, // BA
      layersVisibility: {
        items_self: true,
        couriers_self: true,
        tours_self: true,
        shippings_self: true,
        shippings_shared: true,
        geofences_self: true
      },
      lastFetchTimestamps: {},
      currentCenter: { lat: -34.6037, lng: -58.3816 },
      currentZoom: 13,
      tileProvidersStatic: [],
      layersUpdating: 0,
      newB2bShipping: {},
      modalData: {},
      assignList: [],
      allCouriers: [],
      baseData: null,
      companyCountryConfig: {},
      generalMapPreferences: {
        entities: [],
        timeout: {},
        clustered: {}
      },
      activeFetches: {},
      layerFilters: {},
      activeFiltersLayer: null
    }
  },
  created() {
    this.$root.$on('create-shipping-from-item', this.createShippingFromItem)
    this.$root.$on('data-selected', this.dataSelected)
    this.$root.$on('apply-map-preferences', this.applyUserPreferences)
    this.$root.$on('open-layer-filters', this.openLayerFilters)
  },
  async mounted() {
    this.setupUserPreferences()

    // --- Initial Visibility Logic ---
    // 1. Default visibility from config if not yet set
    this.generalMapPreferencesOptions.forEach(key => {
      if (this.layersVisibility[key] === undefined) {
        const config = this.mapLayersConfig[key]
        this.$set(this.layersVisibility, key, config ? config.visible !== false : true)
      }
    })

    // 2. Forced settings from ecosystem
    if (this.ecosystemMaps && this.ecosystemMaps.generalMapForcedSettings) {
      const forced = this.unflattenObject(this.ecosystemMaps.generalMapForcedSettings)
      if (forced.mapLayers) {
        Object.keys(forced.mapLayers).forEach(layerKey => {
          const layerSettings = forced.mapLayers[layerKey]
          if (Object.hasOwn(layerSettings, 'visible')) {
            this.$set(this.layersVisibility, layerKey, !!layerSettings.visible)
          }
        })
      }
      if (forced.selfShippingsStatuses) {
        if (!this.generalMapPreferences.shippings_self) this.$set(this.generalMapPreferences, 'shippings_self', {})
        this.$set(this.generalMapPreferences.shippings_self, 'statuses', forced.selfShippingsStatuses)
      }
    }
    // --------------------------------

    this.companyCountryConfig = window.countries.find(x => x.iso_code === this.profile.company.country_iso_code).front_config
    this.imperial = this.frontConfig?.i18n?.unitSystem === 'imperial'
    this.metric = !this.imperial

    await this.checkMapProviders()
    this.center = this.frontConfig?.maps?.general?.lat ? { lat: this.frontConfig.maps.general.lat, lng: this.frontConfig.maps.general.lng } : { lat: 0, lng: 0 }

    this.initMap()

    if (this.tileProviders) {
      this.tileProvidersStatic = [...new Set(this.tileProviders)]
      this.setDefaultTileProvider('LeafletMap')
      MapService.setTileProviders(this.tileProvidersStatic)
    }

    // Trigger initial fetching and fit
    await this.applyUserPreferences(true)

    // Smart Polling: Page Visibility API
    document.addEventListener('visibilitychange', this.handleVisibilityChange)
  },
  beforeDestroy() {
    // Clear all scheduled timeouts for allowed layers
    this.generalMapPreferencesOptions.forEach(layerKey => {
      const timeoutKey = `${layerKey}Timeout`
      if (this[timeoutKey]) {
        clearTimeout(this[timeoutKey])
      }
    })

    // Cleanup MapService to free memory and remove listeners
    MapService.destroy()

    // Smart Polling cleanup
    document.removeEventListener('visibilitychange', this.handleVisibilityChange)
    this.$root.$off('apply-map-preferences', this.applyUserPreferences)
    this.$root.$off('open-layer-filters', this.openLayerFilters)
  },
  computed: {
    generalMapPreferencesOptions() {
      return this.getEnabledLayers(this.ecosystemModules)
    }
  },
  watch: {
    generalMapPreferencesOptions: {
      handler(newOptions) {
        // If the ecosystem options change (e.g. profile reload),
        // ensure our active entities are still valid
        if (this.generalMapPreferences.entities) {
          this.generalMapPreferences.entities = this.generalMapPreferences.entities.filter(e =>
            newOptions.includes(e)
          )

          // Also sync visibility for any entity that might have been removed
          Object.keys(this.layersVisibility).forEach(key => {
            if (this.layersVisibility[key] && !newOptions.includes(key)) {
              this.layersVisibility[key] = false
              MapService.toggleLayer(key, false)
            }
          })
        }
      },
      deep: true
    }
  },
  methods: {
    minEntityTimeout(entity) {
      if (!this.mapLayersConfig || !this.mapLayersConfig[entity]) return 0
      return this.mapLayersConfig[entity].timeout || 0
    },
    setupUserPreferences() {
      const preferences = this.getLocalStorage('generalMapPreferences')

      // 1. Sync Technical Settings & Sidebar Presence
      if (preferences) {
        if (Array.isArray(preferences.entities)) {
          this.generalMapPreferences.entities = preferences.entities.filter(e => this.generalMapPreferencesOptions.includes(e))
        }
        if (preferences.timeout) {
          this.generalMapPreferences.timeout = { ...this.generalMapPreferences.timeout, ...preferences.timeout }
        }
        if (preferences.clustered) {
          this.generalMapPreferences.clustered = { ...this.generalMapPreferences.clustered, ...preferences.clustered }
        }
      }

      // 2. Ensure Technical Defaults
      this.generalMapPreferencesOptions.forEach(entity => {
        const config = this.mapLayersConfig[entity]
        if (config) {
          if (this.generalMapPreferences.timeout[entity] === undefined) {
            this.$set(this.generalMapPreferences.timeout, entity, (config.timeout || 60000) / 1000)
          }
          if (this.generalMapPreferences.clustered[entity] === undefined) {
            this.$set(this.generalMapPreferences.clustered, entity, !!config.clustered)
          }
        }
      })

      // Fallback for entities if none saved
      if (!this.generalMapPreferences.entities || !this.generalMapPreferences.entities.length) {
        this.generalMapPreferences.entities = [...this.generalMapPreferencesOptions]
      }

      this.initFiltersFromUrl()
    },
    async applyUserPreferences(fit = false) {
      // 1. Load latest preferences
      this.setupUserPreferences()

      const newClustered = this.generalMapPreferences.clustered || {}

      // 2. Sync visibilities for layers removed from sidebar
      Object.keys(this.layersVisibility).forEach(key => {
        if (this.layersVisibility[key] && !this.generalMapPreferences.entities.includes(key)) {
          this.$set(this.layersVisibility, key, false)
          MapService.toggleLayer(key, false)
        }
      })

      // 3. Trigger fetch for already visible layers
      const fetches = []
      this.generalMapPreferencesOptions.forEach(layerKey => {
        if (this.layersVisibility[layerKey]) {
          // IF CLUSTERING CHANGED: Force a full fetch (re-populate the layer).
          // We compare against the actual rendered layer type instead of a snapshot
          // of the preferences object, because the preferences modal mutates
          // generalMapPreferences.clustered by reference before emitting this event,
          // so a snapshot taken here would already hold the new value.
          const currentClustered = MapService.isLayerClustered(layerKey)
          if (currentClustered !== undefined && currentClustered !== !!newClustered[layerKey]) {
            this.$set(this.lastFetchTimestamps, layerKey, null)
          }

          fetches.push(this.fetchLayer(layerKey))
        }
      })

      if (fetches.length) {
        await Promise.all(fetches)
        if (fit) MapService.fitAll()
      }
    },
    initFiltersFromUrl() {
      const query = this.$route.query
      const filters = {}
      Object.keys(query).forEach(key => {
        if (key.startsWith('f_')) {
          let foundLayer = null
          Object.keys(this.mapLayersConfig).forEach(lk => {
            if (key.startsWith(`f_${lk}_`)) {
              foundLayer = lk
            }
          })

          if (foundLayer) {
            const filterKey = key.replace(`f_${foundLayer}_`, '')
            if (!filters[foundLayer]) filters[foundLayer] = {}

            // Try to parse if it's JSON (for arrays in checkbox-groups)
            let val = query[key]
            try {
              val = JSON.parse(val)
            } catch (e) {
              // Not JSON, keep as string
            }
            filters[foundLayer][filterKey] = val

            // Auto-enable layer if filter has a value
            if (val !== null && val !== '' && !(Array.isArray(val) && val.length === 0)) {
              this.$set(this.layersVisibility, foundLayer, true)
            }
          }
        }
      })
      this.layerFilters = filters
    },
    syncFiltersToUrl() {
      const query = { ...this.$route.query }

      // Clean existing filters from query
      Object.keys(query).forEach(key => {
        if (key.startsWith('f_')) delete query[key]
      })

      // Add current filters
      Object.keys(this.layerFilters).forEach(layerKey => {
        const filters = this.layerFilters[layerKey]
        Object.keys(filters).forEach(filterKey => {
          const val = filters[filterKey]
          if (val !== null && val !== '' && !(Array.isArray(val) && val.length === 0)) {
            const queryKey = `f_${layerKey}_${filterKey}`
            query[queryKey] = typeof val === 'object' ? JSON.stringify(val) : val
          }
        })
      })

      this.$router.replace({ query })
    },
    openLayerFilters(layerKey) {
      this.activeFiltersLayer = layerKey
      this.$nextTick(() => {
        this.$refs.layerFiltersModal.$refs.modal.show()
      })
    },
    applyFilters({ layerKey, filters }) {
      this.$set(this.layerFilters, layerKey, filters)

      // Reset timestamp to force full fetch (avoids stale data when switching filters on/off)
      if (this.lastFetchTimestamps[layerKey]) {
        this.$delete(this.lastFetchTimestamps, layerKey)
      }

      this.syncFiltersToUrl()
      this.syncMapLayerControlFilters()
      // Trigger immediate fetch for the layer
      this.fetchLayer(layerKey)
        .then(() => {
          MapService.fitAll()
        })
    },
    syncMapLayerControlFilters() {
      // This method is called when the layerFilters data property changes.
      // It ensures that the MapLayerControl component (which is a Leaflet control)
      // is updated with the latest filter state.
      // We need to find the MapLayerControl instance and call a method on it
      // to update its internal state.
      const mapLayerControl = MapService.getControlInstance(MapLayerControl)
      if (mapLayerControl && typeof mapLayerControl.updateFilters === 'function') {
        mapLayerControl.updateFilters(this.layerFilters)
      }
    },
    getEnabledLayers(modules) {
      // Only modules with a matching map layer config qualify — ecosystems may
      // enable non-map modules (e.g. deliveryDateMonitor) with the same shape
      const enabledLayers = []
      Object.keys(modules).forEach(key => {
        const val = modules[key]
        if (typeof val === 'object' && val !== null) {
          if (val.self && this.mapLayersConfig[`${key}_self`]) enabledLayers.push(`${key}_self`)
          if (val.shared && this.mapLayersConfig[`${key}_shared`]) enabledLayers.push(`${key}_shared`)
        }
      })
      return enabledLayers
    },
    initMap() {
      // Initialize the map service singleton
      MapService.init(
        'general-map',
        {
          center: [this.center.lat, this.center.lng],
          zoom: 13
        },
        this.$store,
        this,
        this.mapLayersConfig
      )

      MapService.addControl(
        TilesLayerControl,
        null,
        'topright'
      )

      MapService.addControl(
        FitBounds,
        null,
        'topright'
      )

      // Add our new Vue Layer Control
      // Pass store and parent for context inheritance
      MapService.addControl(
        MapLayerControl,
        {
          layersVisibility: this.layersVisibility,
          onVisibilityChange: this.handleLayerVisibilityChange,
          ecosystemModules: this.ecosystemModules,
          activeFilters: this.layerFilters
        },
        'bottomleft',
        this.$store,
        this,
        {
          'apply-filters': this.applyFilters
        }
      )

      MapService.addControl(
        OpenInGoogleMaps,
        null,
        'topleft'
      )

      MapService.addControl(
        AllInOneSearch,
        null,
        'topleft',
        this.$store,
        this,
        {
          'bounds-updated': (bounds) => MapService.fitBounds(bounds),
          'center-updated': (center) => {
            setTimeout(() => {
              MapService.setZoom(16)
              setTimeout(() => {
                MapService.setCenter(center)
              }, 100)
            }, 500)
          }
        }
      )

      MapService.addScaleControl({
        position: 'bottomright',
        metric: this.metric,
        imperial: this.imperial
      })

      MapService.on('moveend', this.setCenter)
      MapService.on('zoomend', this.setZoom)

      // Add map listeners for dynamic layers (like geofences)
      MapService.on('moveend', this.handleMapMove)
      MapService.on('zoomend', this.handleMapMove)
    },
    setDefaultTileProvider(mapName = this.$options.name) {
      if (this.ecosystemMaps?.defaultTileProvider?.[mapName]) {
        this.tileProvidersStatic = this.tileProvidersStatic.map(x => {
          if (x.name === this.ecosystemMaps?.defaultTileProvider?.[mapName]) {
            x.visible = true
          } else {
            x.visible = false
          }
          return x
        })
      }
    },
    setCenter() {
      this.currentCenter = MapService.getCenter()
    },
    setZoom() {
      this.currentZoom = MapService.getZoom()
    },
    handleLayerVisibilityChange(layerKey, visible) {
      this.layersVisibility[layerKey] = visible

      // Trigger fetch when layer becomes visible
      if (visible) {
        this.fetchLayer(layerKey)
          .then(() => {
            MapService.fitAll()
          })
      } else {
        MapService.fitAll()
      }
    },

    /**
     * Generic method to fetch data for any layer
     * @param {string} layerKey - Key from mapLayersConfig (items, moovers, etc)
     */
    async fetchLayer(layerKey) {
      const config = this.mapLayersConfig[layerKey]

      if (!config) {
        console.error(`No config found for layer: ${layerKey}`)
        return
      }

      // Skip if layer is not in enabled options
      if (!this.generalMapPreferencesOptions.includes(layerKey)) {
        return
      }

      // Skip fetch if layer is not visible
      if (!this.layersVisibility[layerKey]) {
        return
      }

      // Special check for geofences zoom level requirement
      if (layerKey === 'geofences_self' && MapService.getZoom() < 13) {
        MapService.updateGeofences(layerKey, null) // Clear if zoom too out
        if (this.geofences) {
          this.geofences = [] // Clear de la memoria de Vue
        }
        return
      }

      if (this.activeFetches[layerKey]) {
        console.log(`[Map] Fetch already in progress for ${layerKey}, skipping.`)
        return
      }

      this.$set(this.activeFetches, layerKey, true)
      this.layersUpdating++
      console.log(`[Map] Start fetching ${layerKey}. Active fetches: ${this.layersUpdating}`)
      try {
        // Use cloneDeep to avoid mutating the original config object
        const params = typeof config.params === 'function'
          ? config.params(this)
          : cloneDeep(config.params)

        if (params === null) return

        // Inject Layer Filters
        const layerFilters = this.layerFilters[layerKey]
        let hasActiveFilters = false

        if (layerFilters && Object.keys(layerFilters).length > 0) {
          // Check if any filter in layerFilters has a meaningful value
          hasActiveFilters = Object.values(layerFilters).some(val =>
            val !== null && val !== '' && !(Array.isArray(val) && val.length === 0)
          )
        }

        console.log(`[Map] Fetching ${layerKey}. hasActiveFilters: ${hasActiveFilters}`, layerFilters)

        if (hasActiveFilters && config.filters && config.filters.items) {
          if (!params.filter_groups) params.filter_groups = []

          // 1. Identify which fields are being overridden by modal filters
          const fieldsToOverride = new Set()
          Object.keys(layerFilters).forEach(fKey => {
            const val = layerFilters[fKey]
            const fDef = config.filters.items[fKey]
            if (fDef && fDef.filter && val !== null && val !== '' && !(Array.isArray(val) && val.length === 0)) {
              const rule = fDef.filter(val)
              // Handle both single rule [field, op, val] and multiple rules [[f1, op, v1], ...]
              if (Array.isArray(rule)) {
                if (rule.length > 0 && Array.isArray(rule[0])) {
                  rule.forEach(r => fieldsToOverride.add(r[0]))
                } else if (rule.length > 0) {
                  fieldsToOverride.add(rule[0])
                }
              }
            }
          })

          // 2. Remove default filters that match those fields
          params.filter_groups.forEach(group => {
            if (group.filters && Array.isArray(group.filters)) {
              group.filters = group.filters.filter(rule => {
                const field = Array.isArray(rule) ? rule[0] : null
                return !fieldsToOverride.has(field)
              })
            }
          })

          // 3. Apply the modal filters in new groups
          Object.keys(layerFilters).forEach(fKey => {
            const val = layerFilters[fKey]
            const fDef = config.filters.items[fKey]
            if (fDef && fDef.filter && val !== null && val !== '' && !(Array.isArray(val) && val.length === 0)) {
              const rule = fDef.filter(val)
              if (Array.isArray(rule)) {
                // If it's an array of rules, push them to a new group
                params.filter_groups.push({ filters: rule.every(r => Array.isArray(r)) ? rule : [rule] })
              }
            }
          })
        }

        // Support incremental updates (Delta) - Force full fetch if filters are active
        const isCurrentlyIncremental = config.isIncremental && !hasActiveFilters
        if (isCurrentlyIncremental && this.lastFetchTimestamps[layerKey]) {
          if (!params.filter_groups) params.filter_groups = [{ filters: [] }]

          // Add a separate filter group for the incremental timestamp to avoid messing with existing filters
          params.filter_groups.push({
            filters: [
              [
                config.timestampField || 'updated_at',
                'gt',
                this.lastFetchTimestamps[layerKey]
              ]
            ]
          })
        }

        const fetchTime = this.moment.utc().format('YYYY-MM-DD HH:mm:ss')

        const response = await this.$http.get(
          config.endpoint(this.$appConfig, this),
          { params }
        )
        if (layerKey === 'couriers_self') {
          this.allCouriers = response.body.data || response.data.data
        }
        const data = config.transform(response.body || response.data, this)

        // Update local state if exists
        // Note: For incremental, we might want to append/merge if the local state is used elsewhere,
        // but since updateMarkers handles the Map internal state, this is mostly for other UI uses.
        if (Object.hasOwn(this.$data, layerKey)) {
          if (config.isIncremental && Array.isArray(this[layerKey])) {
            // Simple merge by ID for local state
            const newDataMap = new Map(data.map(i => [i.id, i]))
            this[layerKey] = [
              ...this[layerKey].filter(i => !newDataMap.has(i.id)),
              ...data
            ]
          } else {
            this[layerKey] = data
          }
        }

        // Update timestamp for next request ONLY if no filters are active (to not break regular deltas)
        if (!hasActiveFilters) {
          this.lastFetchTimestamps[layerKey] = fetchTime
        }

        const clustered = this.generalMapPreferences.clustered[layerKey] !== undefined
          ? this.generalMapPreferences.clustered[layerKey]
          : config.clustered

        if (config.customUpdate) {
          config.customUpdate(data, config, layerKey)
        } else if (config.updateMethod && typeof MapService[config.updateMethod] === 'function') {
          MapService[config.updateMethod](layerKey, data, { ...config, clustered })
        } else {
          // Update map using generic updateMarkers
          MapService.updateMarkers(layerKey, data, {
            markerRenderer: config.markerRenderer,
            markerTooltip: config.markerTooltip,
            markerPopup: config.markerPopup,
            clustered,
            removeMissing: !isCurrentlyIncremental // Don't remove stale markers if incremental
          })
        }

        console.log(`Loaded ${data.length} ${layerKey} (Delta: ${!!config.isIncremental})`)
      } catch (error) {
        console.error(config.fetchErrorMsg || `Error fetching ${layerKey}:`, error)
      } finally {
        this.$delete(this.activeFetches, layerKey)
        this.layersUpdating = Math.max(0, this.layersUpdating - 1)
        console.log(`[Map] Finished fetching ${layerKey}. Active fetches: ${this.layersUpdating}`)
        // Schedule next fetch if timeout is defined
        // We use a property to track the timeout so we can cancel it if visibility changes or component destroys
        if (config.timeout && this.layersVisibility[layerKey] && layerKey !== 'geofences_self') {
          const timeoutKey = `${layerKey}Timeout`
          if (this[timeoutKey]) clearTimeout(this[timeoutKey])

          let nextTimeout = config.timeout

          // Override with user preference if exists
          if (this.generalMapPreferences.timeout[layerKey]) {
            nextTimeout = Number(this.generalMapPreferences.timeout[layerKey]) * 1000
          }

          // Smart Polling: If enabled for this layer and page is in background, reduce frequency
          if (config.smartPolling && document.hidden) {
            // Increase timeout (e.g., 6x original or minimum 60s)
            nextTimeout = Math.max(config.timeout * 6, 60000)
          }

          console.log(`[Map] Scheduling next fetch for ${layerKey} in ${nextTimeout}ms`)
          this[timeoutKey] = setTimeout(() => this.fetchLayer(layerKey), nextTimeout)
        }
      }
    },

    /**
     * Handle map movement to refresh dynamic layers like geofences
     */
    handleMapMove: debounce(function () {
      if (this.layersVisibility.geofences_self) {
        this.fetchLayer('geofences_self')
      }
    }, 500),

    /**
     * Smart Polling: React to page visibility changes
     */
    handleVisibilityChange() {
      if (!document.hidden) {
        // Tab became visible: Resume nominal frequency and trigger immediate refresh for smart layers
        this.generalMapPreferencesOptions.forEach(layerKey => {
          const config = this.mapLayersConfig[layerKey]
          if (config && config.smartPolling && config.timeout && this.layersVisibility[layerKey]) {
            const timeoutKey = `${layerKey}Timeout`
            if (this[timeoutKey]) clearTimeout(this[timeoutKey])

            // Trigger fetch without waiting for the next scheduled tick
            this.fetchLayer(layerKey)
          }
        })
      }
    },
    createShippingFromItem(data) {
      this.newB2bShipping = {
        type: 'regular',
        flow: 'semi-automatic',
        size: 'palet',
        from: {
          coords: {
            lat: data.item_tracking.lat,
            lng: data.item_tracking.lng
          },
          addressDescription: data.item_tracking.meta.address,
          contact: {
            firstName: data.item_tracking.meta.geofenceName || this.profile.company.contact_name,
            lastName: '',
            email: this.profile.company.email,
            phone: this.profile.company.phone
          },
          message: ''
        },
        to: {
          placeId: null,
          coords: {
            lat: null,
            lng: null
          },
          addressDescription: '',
          contact: {
            firstName: '',
            lastName: '',
            email: '',
            phone: ''
          },
          message: ''
        },
        internalCode: '',
        description: '',
        conf: {
          assurance: false
        },
        items: [
          { ...data, ...{ item_id: data.id } }
        ]
      }
      this.$refs.modalShippingsCreateFromItem.show()
    },
    shippingCreated() {
      this.$refs.modalShippingsCreateFromItem.hide()
    },
    dataSelected(data) {
      this.$refs.visitsOverview.show(data)
    },
    handleAssignmentDone() {
      // Refresca solo las capas autorizadas que estén activas
      this.generalMapPreferencesOptions.forEach(layerKey => {
        if (this.layersVisibility[layerKey]) {
          const timeoutKey = `${layerKey}Timeout`
          if (this[timeoutKey]) clearTimeout(this[timeoutKey])
          this.fetchLayer(layerKey)
        }
      })
    }
  }
}
</script>

<style>
/* Page Styles */
.map-demo-page {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  flex-direction: column;
}

.controls-panel {
  padding: 10px;
  background: white;
  z-index: 1000;
  border-bottom: 1px solid #ddd;
}

.map-container {
  flex: 1;
  width: 100%;
  height: 500px; /* Fallback height */
  min-height: 400px;
  position: relative; /* Ensure it creates a stacking context */
  z-index: 1;
}

/* Map Marker Styles (L.divIcon) */
.tour-stop-icon {
  background: #333;
  color: white;
  border-radius: 50%;
  text-align: center;
  line-height: 24px;
  font-weight: bold;
  font-size: 12px;
  border: 2px solid white;
  box-shadow: 0 2px 5px rgba(0, 0, 0, 0.5);
}

.item-marker {
  width: 20px;
  height: 20px;
  border-radius: 50%;
  border: 2px solid white;
}
.item-marker.active {
  background: #28a745;
}
.item-marker.inactive {
  background: #dc3545;
}
.tour-stop-pin {
  background: #55b2e1;
  color: white;
  border-radius: 3px;
  min-width: 21px;
  width: fit-content;
  height: 18px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 9px;
  border: 1px solid rgba(255, 255, 255, 0.5);
  /* box-shadow: 0 2px 4px rgba(255, 255, 255, 0.5); */
  position: relative;
}
</style>
